/**
 * Threats on the map: two rings each, drawn so a commander reads danger and
 * standoff at a glance.
 *
 * The lethal ring is a filled red circle — the area to keep aircraft out of.
 * The warning ring is a larger dashed amber outline with no fill, so it frames
 * the standoff without hiding the ground inside it. Each threat is labelled
 * with its name, because an unlabelled red circle is a question, not a warning.
 *
 * These come from the shared server list, so every operator sees the same
 * threats — the whole reason they are not client-side scratch.
 */

import { useEffect } from 'react';

import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { circleRing } from '@/map/useZoneLayer';
import { useThreatStore } from '@/stores/threatStore';
import type { Threat } from '@/api/threats';

export const THREAT_SOURCE_ID = 'threats';
const THREAT_LETHAL_FILL = 'threats-lethal-fill';
const THREAT_LETHAL_LINE = 'threats-lethal-line';
const THREAT_WARNING_LINE = 'threats-warning-line';
const THREAT_LABEL = 'threats-label';

const LETHAL = '#f0563f';
const WARNING = '#f0a53f';

export function threatFeatures(threat: Threat): unknown[] {
  return [
    {
      type: 'Feature',
      properties: { ring: 'lethal', name: threat.name, kind: threat.kind },
      geometry: {
        type: 'Polygon',
        coordinates: [circleRing(threat.lat, threat.lon, threat.lethalRadiusM)],
      },
    },
    {
      type: 'Feature',
      properties: { ring: 'warning', name: threat.name, kind: threat.kind },
      geometry: {
        type: 'Polygon',
        coordinates: [circleRing(threat.lat, threat.lon, threat.warningRadiusM)],
      },
    },
  ];
}

export interface ThreatLayer {
  destroy: () => void;
  stats: () => { redraws: number; threats: number };
}

export function createThreatLayer(map: MapLike): ThreatLayer {
  let redraws = 0;
  let threatCount = 0;

  if (!map.getSource(THREAT_SOURCE_ID)) {
    map.addSource(THREAT_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(THREAT_LETHAL_FILL)) {
    map.addLayer({
      id: THREAT_LETHAL_FILL,
      type: 'fill',
      source: THREAT_SOURCE_ID,
      filter: ['==', ['get', 'ring'], 'lethal'],
      // Low fill so imagery stays readable; the point is to mark the danger,
      // not black it out.
      paint: { 'fill-color': LETHAL, 'fill-opacity': 0.18 },
    });
  }

  if (!map.getLayer(THREAT_LETHAL_LINE)) {
    map.addLayer({
      id: THREAT_LETHAL_LINE,
      type: 'line',
      source: THREAT_SOURCE_ID,
      filter: ['==', ['get', 'ring'], 'lethal'],
      paint: { 'line-color': LETHAL, 'line-width': 2 },
    });
  }

  if (!map.getLayer(THREAT_WARNING_LINE)) {
    map.addLayer({
      id: THREAT_WARNING_LINE,
      type: 'line',
      source: THREAT_SOURCE_ID,
      filter: ['==', ['get', 'ring'], 'warning'],
      // Dashed and unfilled: it frames the standoff without hiding the ground.
      paint: { 'line-color': WARNING, 'line-width': 1.5, 'line-dasharray': [3, 2] },
    });
  }

  if (!map.getLayer(THREAT_LABEL)) {
    map.addLayer({
      id: THREAT_LABEL,
      type: 'symbol',
      source: THREAT_SOURCE_ID,
      filter: ['==', ['get', 'ring'], 'lethal'],
      layout: { 'text-field': ['get', 'name'], 'text-size': 11, 'symbol-placement': 'line' },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  const draw = (threats: Threat[]) => {
    const source = geoJsonSource(map, THREAT_SOURCE_ID);
    if (!source) return;
    source.setData({ type: 'FeatureCollection', features: threats.flatMap(threatFeatures) });
    threatCount = threats.length;
    redraws += 1;
  };

  const unsubscribe = useThreatStore.subscribe((state) => draw(state.threats));
  draw(useThreatStore.getState().threats);

  return {
    destroy: () => {
      unsubscribe();
      for (const id of [THREAT_LABEL, THREAT_WARNING_LINE, THREAT_LETHAL_LINE, THREAT_LETHAL_FILL]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(THREAT_SOURCE_ID)) map.removeSource(THREAT_SOURCE_ID);
    },
    stats: () => ({ redraws, threats: threatCount }),
  };
}

export function useThreatLayer(): void {
  const controller = useMapController();
  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createThreatLayer(map);
        return () => layer.destroy();
      }),
    [controller],
  );
}
